import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import { ROUTE_APPROVAL_RULES, ROUTE_CLAIM_A_DOMAIN } from '../utils/constants';

const messages = defineMessages({
  title: {
    id: 'Security.home.title',
    defaultMessage: 'Security',
    description: 'Title for the workspace Security page',
  },
  approvalRulesTitle: {
    id: 'Security.home.approvalRulesTitle',
    defaultMessage: 'Approval rules',
    description: 'Title for the approval rules entry point on the workspace Security page',
  },
  approvalRulesDescription: {
    id: 'Security.home.approvalRulesDescription',
    defaultMessage: 'Require approvals for sensitive actions (eg. transfers, changes)',
    description: 'Description for the approval rules entry point on the workspace Security page',
  },
  claimADomainTitle: {
    id: 'Security.home.claimADomainTitle',
    defaultMessage: 'Claim a domain',
    description: 'Title for the claim a domain entry point on the workspace Security page',
  },
  claimADomainDescription: {
    id: 'Security.home.claimADomainDescription',
    defaultMessage: "Verify your organization's domain",
    description: 'Description for the claim a domain entry point on the workspace Security page',
  },
});

export function SecurityPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isWorkspaceConsensusTreatment = useIsTreatment(AppExperiments.t0018_7b870e9f);
  const isClaimADomainTreatment = useIsTreatment(AppExperiments.t0018_1a1ab525);

  const handleApprovalRulesClick = useCallback(() => {
    navigate(ROUTE_APPROVAL_RULES);
  }, [navigate]);

  const handleClaimADomainClick = useCallback(() => {
    navigate(ROUTE_CLAIM_A_DOMAIN);
  }, [navigate]);

  const approvalRulesTitle = useMemo(
    () => formatMessage(messages.approvalRulesTitle),
    [formatMessage],
  );
  const claimADomainTitle = useMemo(
    () => formatMessage(messages.claimADomainTitle),
    [formatMessage],
  );

  return (
    <VStack gap={4} width="100%" data-testid="security-page">
      <Text as="h1" font="title1">
        {formatMessage(messages.title)}
      </Text>

      {isWorkspaceConsensusTreatment ? (
        <ListCell
          accessory="arrow"
          spacingVariant="condensed"
          media={<Icon name="api" size="s" color="fg" />}
          title={approvalRulesTitle}
          description={formatMessage(messages.approvalRulesDescription)}
          onClick={handleApprovalRulesClick}
          accessibilityLabel={approvalRulesTitle}
          testID="security-approval-rules-entry"
        />
      ) : null}

      {isClaimADomainTreatment ? (
        <ListCell
          accessory="arrow"
          spacingVariant="condensed"
          media={<Icon name="globe" size="s" color="fg" />}
          title={claimADomainTitle}
          description={formatMessage(messages.claimADomainDescription)}
          onClick={handleClaimADomainClick}
          accessibilityLabel={claimADomainTitle}
          testID="security-claim-a-domain-entry"
        />
      ) : null}
    </VStack>
  );
}
